export function getBookingScrollContainer(element: HTMLElement) {
  return element.closest<HTMLElement>('[data-native-route-page]');
}

/** Offset is measured from the viewport, including any fixed app header. */
export function scrollToBookingElement(element: HTMLElement, offset: number) {
  const container = getBookingScrollContainer(element);
  const containerTop = container?.getBoundingClientRect().top ?? 0;
  const scrollTop = container?.scrollTop ?? window.scrollY;
  const top =
    element.getBoundingClientRect().top +
    scrollTop -
    Math.max(offset, containerTop);
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'instant'
    : 'smooth';

  if (container) container.scrollTo({ top, behavior });
  else window.scrollTo({ top, behavior });
}

/**
 * Scrolls down only far enough for `end` to sit at the bottom of the view,
 * never up. On the web it stops once `start` reaches the top offset (a section
 * taller than the view). In the native app it always brings `end` into view,
 * because the pay button is the point there. Wherever the fixed bottom tab bar
 * shows (native app, mobile web), its top edge counts as the bottom of the view.
 */
export function revealBookingRange(
  start: HTMLElement,
  end: HTMLElement,
  offset: number,
  bottomGap = 16,
) {
  const container = getBookingScrollContainer(start);
  const containerRect = container?.getBoundingClientRect();
  const viewTop = Math.max(offset, containerRect?.top ?? 0);
  // The fixed bottom tab bar (native app and mobile web) covers the bottom
  // of the view; a hidden one reports no box. The iPhone Duo side rail
  // covers no part of the bottom.
  const tabBar = document.querySelector(
    '[data-native-tab-bar]:not([data-layout="rail"])',
  );
  const tabBarTop = tabBar?.getClientRects().length
    ? tabBar.getBoundingClientRect().top
    : undefined;
  const viewBottom = Math.min(
    containerRect?.bottom ?? window.innerHeight,
    tabBarTop ?? Number.POSITIVE_INFINITY,
  );
  const scrollTop = container?.scrollTop ?? window.scrollY;

  const toEnd = end.getBoundingClientRect().bottom - (viewBottom - bottomGap);
  const distance = container
    ? toEnd
    : Math.min(toEnd, start.getBoundingClientRect().top - viewTop);
  if (distance <= 0) return;

  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'instant'
    : 'smooth';
  const top = scrollTop + distance;

  if (container) container.scrollTo({ top, behavior });
  else window.scrollTo({ top, behavior });
}

/**
 * revealBookingRange now, then again whenever the booking form grows over the
 * next `windowMs` (courts, payment and add-ons render after async loads, which
 * on a phone can take a few seconds), so
 * the reveal lands on the final layout. Only ever scrolls down; the first
 * wheel, touch or key from the customer stops it. Returns a cancel function.
 */
export function followBookingRange(
  start: HTMLElement,
  end: HTMLElement,
  offset: number,
  windowMs = 4000,
) {
  let stopped = false;
  // A resize already queued when the customer takes over must not scroll.
  const reveal = () => {
    if (!stopped) revealBookingRange(start, end, offset);
  };
  reveal();

  const form = start.closest<HTMLElement>('[data-native-booking-form]');
  const observer = new ResizeObserver(reveal);
  observer.observe(form ?? document.body);

  const userEvents = ['wheel', 'touchstart', 'keydown'] as const;
  const stop = () => {
    stopped = true;
    observer.disconnect();
    window.clearTimeout(timer);
    userEvents.forEach((type) => window.removeEventListener(type, stop));
  };
  const timer = window.setTimeout(stop, windowMs);
  userEvents.forEach((type) =>
    window.addEventListener(type, stop, { passive: true, once: true }),
  );

  return stop;
}
